/**
 * Browser-side `list_imported_patients` and `check_pacs_connection`: the
 * connector's own record of imported patients and its PACS configuration,
 * through existing connector routes (injected, so this is React-free and
 * unit-testable). Per-slice data is reduced to counts.
 */
import type { PatientPresentation } from '~/backend/connector-api/connector-schemas';
import type {
    CheckPacsConnectionOutput,
    ListImportedPatientsOutput,
} from '../../contract';
import { asRecord } from '../core';

/** Same cap as the server contract (MAX_IMPORTED_PATIENTS_RETURNED). */
export const MAX_IMPORTED_PATIENTS_RETURNED = 50;

export interface ConnectorStoreDeps {
    /** False outside connector mode: there is no connector to ask. */
    readonly connectorMode: boolean;
    /** `GET /api/patients` — every imported patient. IDs and names only:
     *  the route does not load stored images/structure sets. */
    listImportedPatients(): Promise<PatientPresentation[]>;
    /** `GET /api/patients/{patientId}` — one patient's full stored record. */
    getImportedPatient(patientId: string): Promise<PatientPresentation>;
    /** `GET /api/dicom/targets` — target name → flow direction. */
    listTargets(): Promise<Record<string, string>>;
    /** `GET /api/dicom/echo` — resolves when EVERY target answered,
     *  rejects when any failed (the route is all-or-nothing). */
    echoAllTargets(): Promise<void>;
}

const NOT_CONNECTOR =
    'this needs the viewer in connector mode (connected through the ' +
    'Workspace+ Connector)';

function summary(p: PatientPresentation) {
    return {
        patientId: p.patientID ?? '',
        patientName: p.patientName ?? '',
        imageSeriesCount: p.images?.length ?? 0,
        structureSetCount: p.structureSets?.length ?? 0,
        planCount: p.plans?.length ?? 0,
        doseCount: p.doses?.length ?? 0,
        registrationCount: p.registrations?.length ?? 0,
        taskCount: p.tasks?.length ?? 0,
    };
}

interface StoredObject {
    seriesInstanceUID?: string;
    modality?: string;
    seriesDateTime?: string | null;
    syncStatus?: string;
}

function stored(o: StoredObject) {
    return {
        seriesInstanceUid: o.seriesInstanceUID ?? '',
        modality: o.modality ?? '',
        seriesDateTime: o.seriesDateTime ?? '',
        syncStatus: o.syncStatus ?? '',
    };
}

export async function listImportedPatients(
    input: unknown,
    deps: ConnectorStoreDeps
): Promise<ListImportedPatientsOutput> {
    if (!deps.connectorMode) {
        return { status: 'unavailable', reason: NOT_CONNECTOR };
    }
    const raw = asRecord(input)?.patientId;
    const patientId = typeof raw === 'string' && raw ? raw : undefined;
    let patients: PatientPresentation[];
    try {
        patients = (await deps.listImportedPatients()).filter(
            (p) => !!p.patientID
        );
    } catch {
        return {
            status: 'unavailable',
            reason: 'could not read the imported patients from the connector',
        };
    }

    if (!patientId) {
        return {
            status: 'list',
            patients: patients
                .slice(0, MAX_IMPORTED_PATIENTS_RETURNED)
                .map((x) => ({
                    patientId: x.patientID ?? '',
                    patientName: x.patientName ?? '',
                })),
            truncated: patients.length > MAX_IMPORTED_PATIENTS_RETURNED,
        };
    }
    // Membership first: the detail route's "not found" is indistinguishable
    // from a server error in the generated client.
    if (!patients.some((x) => x.patientID === patientId)) {
        return {
            status: 'unavailable',
            reason:
                `patient "${patientId}" has not been imported into ` +
                'Workspace+ (it may still exist in the PACS; use ' +
                'search_patients)',
        };
    }
    let p: PatientPresentation;
    try {
        p = await deps.getImportedPatient(patientId);
    } catch {
        return {
            status: 'unavailable',
            reason: `could not read the stored record of "${patientId}" from the connector`,
        };
    }
    return {
        status: 'detail',
        patient: summary(p),
        images: (p.images ?? []).map((i) => ({
            seriesInstanceUid: i.seriesInstanceUID ?? '',
            modality: i.modality ?? '',
            seriesDateTime: i.seriesDateTime ?? '',
            sliceCount: i.slices?.length ?? 0,
        })),
        structureSets: (p.structureSets ?? []).map(stored),
        plans: (p.plans ?? []).map(stored),
        doses: (p.doses ?? []).map(stored),
        registrations: (p.registrations ?? []).map(stored),
        tasks: (p.tasks ?? []).map((t) => ({
            modelName: t.modelName ?? '',
            status: t.status ?? '',
            createdAt: t.createdAt ?? '',
        })),
    };
}

export async function checkPacsConnection(
    deps: ConnectorStoreDeps
): Promise<CheckPacsConnectionOutput> {
    if (!deps.connectorMode) {
        return { status: 'unavailable', reason: NOT_CONNECTOR };
    }
    let targets: Record<string, string>;
    try {
        targets = await deps.listTargets();
    } catch {
        return {
            status: 'unavailable',
            reason: 'the connector is not reachable from the viewer',
        };
    }
    let echo: 'all-reachable' | 'not-all-reachable';
    try {
        await deps.echoAllTargets();
        echo = 'all-reachable';
    } catch {
        echo = 'not-all-reachable';
    }
    return {
        status: 'ok',
        targets: Object.entries(targets ?? {}).map(([name, direction]) => ({
            name,
            direction: String(direction),
        })),
        echo,
    };
}
